import React, { useCallback, useMemo, useState } from 'react'
import { COMPACT_PLANETS_KEY, COMPACT_TRAVEL_KEY, compactPrefKey, parseCompactPref, isCompactListActive } from './compactViewPref'
import { useSelector } from '../../../../hooks/useSelector'
import { Icon } from 'semantic-ui-react'
import groupBy from 'lodash/groupBy'
import get from 'lodash/get'
import translateOrbitingPlanet from '../../../../utils/translateOrbitingPlanet'
import RiftChoice from './RiftChoice'
import { GameBoard, PlanetDeed } from '@prometheus/types'
import { PlanetDetailsDisplay } from './PlanetDetailsDisplay'
import './PlayerChoosePlanetMovement.scss'
import { useDispatch } from 'react-redux'
import { setFullscreen, setSortOwned } from '../../../../reducers/game/game'
import usePlayerActions from '../../hooks/usePlayerActions'
import { truncateCash } from '@prometheus/utils'
import classNames from 'classnames'
import { Box, Button, Card, Divider, Popup, Stack, Typography } from '@prometheus/components'
import PromiseButtonV2 from '@/components/PromiseButtonV2'
import translateOrbitingPlanetColorRaw from '@/utils/translateOrbitingPlanetColorRaw'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { CompactPlanetCard } from './CompactPlanetCard'
import { isCompactEligible } from './compactPlanet'

// Compact/Full view preferences; survive tab switches (panels unmount) and
// sessions. Owned list and travel-choice list are toggled independently, and
// each device class remembers its own choice (rules in `compactViewPref.ts`:
// phones default compact on the original keys, desktop defaults to full
// cards on `-desktop` keys).
const readCompactPref = (key: string, isMobile: boolean) => {
  try {
    return parseCompactPref(localStorage.getItem(key), isMobile)
  } catch {
    return isMobile
  }
}

const writeCompactPref = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, String(value))
  } catch {
    // Private browsing without storage — the in-memory state still works.
  }
}

export default ({ owned, fullscreen }: { owned?: boolean, fullscreen?: boolean }) => {
  const uid = useSelector(s => s.auth.uid)
  const players_choosing_planet = (useSelector(s => s.game.metadata?.players_choosing_planet) || [])
  const has_rift_in_choice = useSelector(s => s.game.sheet?.has_rift_in_choice)
  const planet_movement_options = useSelector(s => s.game.sheet?.planet_movement_options)
  const planet_purchase_options = useSelector(s => s.game.sheet?.planet_purchase_options)
  const planets = useSelector(s => s.game.sheet?.planets)
  const current_planet = useSelector(s => get(s, `game.metadata.players.${uid}.current_planet`)) as keyof GameBoard
  const is_fullscreen = (useSelector(s => s.game.fullscreen))
  const is_map = useSelector(s => s.game.galaxy_display)
  const sort_owned = useSelector(s => s.game.sort_owned)
  const rift_opening_year = useSelector(s => s.game.metadata?.rift_opening_year)
  const { onUpgradePlanet } = usePlayerActions()
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  // Held as a map keyed by storage key, so a window resized across the phone
  // boundary shows that device class's own saved choice (the fallback reads
  // re-run when the key changes) instead of dragging one preference across
  // both layouts.
  const [compactPrefs, setCompactPrefs] = useState<{ [key: string]: boolean }>({})
  const planetsPrefKey = compactPrefKey(COMPACT_PLANETS_KEY, isMobile)
  const travelPrefKey = compactPrefKey(COMPACT_TRAVEL_KEY, isMobile)
  const storedCompactView = useMemo(() => readCompactPref(planetsPrefKey, isMobile), [planetsPrefKey, isMobile])
  const storedCompactTravelView = useMemo(() => readCompactPref(travelPrefKey, isMobile), [travelPrefKey, isMobile])
  const compactView = compactPrefs[planetsPrefKey] ?? storedCompactView
  const compactTravelView = compactPrefs[travelPrefKey] ?? storedCompactTravelView

  const onToggleCompact = useCallback((value: boolean) => {
    setCompactPrefs(prefs => ({ ...prefs, [planetsPrefKey]: value }))
    writeCompactPref(planetsPrefKey, value)
  }, [planetsPrefKey])

  const onToggleCompactTravel = useCallback((value: boolean) => {
    setCompactPrefs(prefs => ({ ...prefs, [travelPrefKey]: value }))
    writeCompactPref(travelPrefKey, value)
  }, [travelPrefKey])

  const onSort = useCallback((sort: string) => {
    dispatch(setSortOwned(sort))
  }, [dispatch])

  const totalLoanAvailable = useMemo(() => {
    let total = 0

    if (!owned || !planets) {
      return total
    }

    for (const p of Object.values(planets)) {
      if (p.loan === p.level) {
        continue
      }

      if (p.loan === -1) {
        total += p.deed.levels[p.level].loan
      } else {
        total += Math.max(0, p.deed.levels[p.level].loan - p.deed.levels[p.loan].loan)
      }
    }

    return total
  }, [planets, owned])

  const totalLoanUsed = useMemo(() => {
    let total = 0

    if (!owned || !planets) {
      return total
    }

    for (const p of Object.values(planets)) {
      if (p.loan > -1) {
        total += p.deed.levels[p.loan].loan
      }
    }

    return total
  }, [planets, owned])

  if (fullscreen && !is_fullscreen) {
    return null
  }

  if (!planet_purchase_options || !rift_opening_year) {
    return null
  }

  if (!planet_movement_options) {
    return null
  }

  if (!Object.keys(planet_purchase_options).length && !Object.keys(planet_movement_options).length && !owned) {
    return null
  }

  if (!planets) {
    return null
  }

  if (!players_choosing_planet && !Object.keys(planet_purchase_options).length && !owned) {
    return null
  }

  if (!current_planet) {
    return null
  }

  if (owned && planets && !Object.keys(planets).length) {
    return null
  }

  const isPurchase = !!Object.keys(planet_purchase_options).length

  let relevant_planets
  if (owned) {
    relevant_planets = planets
  } else if (isPurchase) {
    relevant_planets = planet_purchase_options
  } else {
    relevant_planets = planet_movement_options
  }

  let planet_options = groupBy(Object.keys(relevant_planets).map(planet_data => {
    const [region_info, planet_info] = planet_data.split(':')
    const [orbiting_planet, quadrant] = region_info.split('|')
    const [planet_name, data] = planet_info.split('|')

    let deed
    if (owned) {
      deed = planets[planet_data].deed
    } else if (isPurchase) {
      deed = planet_purchase_options[planet_data]
    } else {
      deed = planet_movement_options[planet_data]
    }

    return {
      isPlanet: ['Starbase', 'Battlestation'].indexOf(planet_name) === -1,
      planet_name,
      data,
      orbiting_planet,
      quadrant,
      original: planet_data as keyof GameBoard,
      deed,
      ownedPlanet: planets[planet_data],
      purchased_at: planets[planet_data]?.purchased_at,
    }
  }), 'orbiting_planet')

  let sorted_keys: string[] = Object.keys(planet_options).filter(p => p !== 'R')
  if (owned && sort_owned === 'latest') {
    planet_options = {
      'All Planets': Object.values(planet_options).flat(),
    }
    planet_options['All Planets'].sort((a, b) => {
      return (b.purchased_at ?? 0) - (a.purchased_at ?? 0)
    })
    sorted_keys = ['All Planets']
  } else if (owned && sort_owned === 'planet') {
    planet_options = {
      'All Planets': Object.values(planet_options).flat(),
    }
    planet_options['All Planets'].sort((a, b) => {
      const bd = (b.deed as PlanetDeed).levels
      const ad = (a.deed as PlanetDeed).levels
      return bd[bd.length - 1].pop - ad[ad.length - 1].pop
    })
    sorted_keys = ['All Planets']
  } else if ((owned && sort_owned === 'region') || !owned) {
    for (const value of Object.values(planet_options)) {
      value.sort((a, b) => a.planet_name.localeCompare(b.planet_name))
    }
    sorted_keys.sort((a, b) => rift_opening_year[b] - rift_opening_year[a])
  } else if (owned && sort_owned === 'upgrade') {
    planet_options = {
      'All Planets': Object.values(planet_options).flat(),
    }
    planet_options['All Planets'].sort((a, b) => {
      const bv = b.ownedPlanet.sub_levels_current - b.ownedPlanet.tile
      const av = a.ownedPlanet.sub_levels_current - a.ownedPlanet.tile
      return bv - av
    })
    sorted_keys = ['All Planets']
  } else {
    for (const value of Object.values(planet_options)) {
      value.sort((a, b) => a.planet_name.localeCompare(b.planet_name))
    }
    sorted_keys.sort((a, b) => a.localeCompare(b))
  }

  const current_orbiting_planet = current_planet.split(':')[0].split('|')[0]

  delete planet_options.R

  // Compact mode — default on phones, opt-in on desktop — applies to the
  // owned list and the travel-choice list. Besides swapping cards it also
  // flattens the quadrant grouping (no orbit headers) so compact cards pack
  // side by side across regions. A single eligible card has nothing to sit
  // beside — it renders as the full card.
  const compactEligibleCount = Object.values(planet_options).flat()
    .filter(p => isCompactEligible(p.deed?.type, owned || false, !!p.ownedPlanet)).length
  const flattenCompact = isCompactListActive(owned || false, isPurchase, compactView, compactTravelView, compactEligibleCount)

  const renderPlanet = (planet: (typeof planet_options)[string][number]) => {
    if (isPurchase && planet.original.indexOf('Stargate') !== -1) {
      return null
    }

    // Slim live cards for deed planets (starbases/stargates keep the full
    // card — they have no level table to compact).
    if (flattenCompact && isCompactEligible(planet.deed?.type, owned || false, !!planet.ownedPlanet)) {
      return (
        <CompactPlanetCard
          key={planet.original}
          planet={planet.original}
          deed={planet.deed as PlanetDeed}
          owned={owned || false}
          ownedPlanet={planet.ownedPlanet}
        />
      )
    }

    return (
      <PlanetDetailsDisplay choice
        key={planet.original}
        planet={planet.original}
        deed={planet.deed}
        isPurchase={isPurchase}
        owned={owned || false}
        ownedPlanet={planet.ownedPlanet}
        fullscreen={fullscreen}
      />
    )
  }

  return (
    <Card
      padding='none'
      transparent
      className={classNames({ is_map })}
      style={{
        zIndex: 1,
        position: 'relative',
        ...(owned ? {
          borderLeft: 'none',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        } : {})
      }}
    >
      {owned && <Icon onClick={() => dispatch(setFullscreen(!is_fullscreen))} style={{ position: 'absolute', top: '14px', right: '10px', cursor: 'pointer', color: 'var(--prometheus-secondary)', margin: 0 }} name="expand" size="large" />}
      <Box space="sm">
        <Typography variant="h3" retro color="secondary">
          {owned && 'PLANETS'}
          {!owned && !isPurchase && 'CHOOSE PLANET'}
          {!owned && !!isPurchase && 'CHOOSE ACTION'}
        </Typography>
      </Box>
      <Divider />
      {owned && (
        <React.Fragment>
          <Box space="sm">
            <Stack direction="row" space="sm">
              <PromiseButtonV2 disabled={totalLoanAvailable === 0} ignoreGameLogic size="sm" variant="danger" outline onClick={() => onUpgradePlanet(null, 'all_planet_loans')}>
                Withdraw All ({truncateCash(totalLoanAvailable, { raw: true })})
              </PromiseButtonV2>
              <PromiseButtonV2 disabled={totalLoanUsed === 0} ignoreGameLogic size="sm" color="primary" outline onClick={() => onUpgradePlanet(null, 'deposit_all_planet_loans')}>
                Deposit All ({truncateCash(totalLoanUsed, { raw: true })})
              </PromiseButtonV2>
            </Stack>
          </Box>
          <Divider />
          <Box space="sm">
            <Stack space="sm">
              <Typography variant="h5">Sorting</Typography>
              <Stack style={{ flexWrap: 'wrap' }} direction="row" space="xs">
                <Button size="xs" variant={!sort_owned ? 'primary' : 'info'} onClick={() => onSort('')}>Default</Button>
                <Button size="xs" variant={sort_owned === 'region' ? 'primary' : 'info'} onClick={() => onSort('region')}>Region Size</Button>
                <Button size="xs" variant={sort_owned === 'planet' ? 'primary' : 'info'} onClick={() => onSort('planet')}>Planet Size</Button>
                <Button size="xs" variant={sort_owned === 'upgrade' ? 'primary' : 'info'} onClick={() => onSort('upgrade')}>Upgrading</Button>
                <Button size="xs" variant={sort_owned === 'latest' ? 'primary' : 'info'} onClick={() => onSort('latest')}>Latest</Button>
              </Stack>
              <Stack style={{ flexWrap: 'wrap', alignItems: 'center' }} direction="row" space="xs">
                <Typography variant="h5" component="span" style={{ marginRight: '4px' }}>View</Typography>
                <Button size="xs" variant={compactView ? 'primary' : 'info'} onClick={() => onToggleCompact(true)}>Compact</Button>
                <Button size="xs" variant={!compactView ? 'primary' : 'info'} onClick={() => onToggleCompact(false)}>Full</Button>
              </Stack>
            </Stack>
          </Box>
          <Divider />
        </React.Fragment>
      )}
      {!owned && !isPurchase && (
        <React.Fragment>
          <Box space="sm">
            <Stack style={{ flexWrap: 'wrap', alignItems: 'center' }} direction="row" space="xs">
              <Typography variant="h5" component="span" style={{ marginRight: '4px' }}>View</Typography>
              <Button size="xs" variant={compactTravelView ? 'primary' : 'info'} onClick={() => onToggleCompactTravel(true)}>Compact</Button>
              <Button size="xs" variant={!compactTravelView ? 'primary' : 'info'} onClick={() => onToggleCompactTravel(false)}>Full</Button>
            </Stack>
          </Box>
          <Divider />
        </React.Fragment>
      )}
      {has_rift_in_choice && !owned && (<RiftChoice />)}
      {flattenCompact ? (
        // Compact view: skip the planetary-quadrant headers and pour every
        // card into one wrapping row so they pack side by side.
        <Box space="sm">
          <Stack space="sm" direction="row" style={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
            {sorted_keys.flatMap(k => planet_options[k]).map(renderPlanet)}
          </Stack>
        </Box>
      ) : sorted_keys.map((orbiting_planet, i, arr) => {
        const color = translateOrbitingPlanetColorRaw(orbiting_planet)
        const isLast = arr.length === i+1;
        return (
          <React.Fragment key={`${orbiting_planet}${i}`}>
            <Box space="sm">
              <Stack direction="row" space="xs" style={{ alignItems: 'center' }}>
                <Icon style={{ marginTop: '2px', marginRight: '6px', color }} name="globe" size={owned ? 'big' : 'huge'} />
                <Typography style={{ color }} variant={owned ? 'h4' : 'h2'} retro uppercase>{translateOrbitingPlanet(orbiting_planet)}</Typography>
                {orbiting_planet === current_orbiting_planet && (
                  <Popup
                    position="right center"
                    trigger={(
                      <Icon style={{ color: 'var(--prometheus-primary)' }} name="rocket" size={owned ? 'large' : 'big'} />
                    )}
                  >
                    <Card padding="sm">Current Orbit</Card>
                  </Popup>
                )}
              </Stack>
            </Box>
            <Divider />
            <Box space="sm">
              <Stack space="sm" direction="row" style={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                {planet_options[orbiting_planet].map(renderPlanet)}
              </Stack>
            </Box>
            {!isLast && (<Divider />)}
          </React.Fragment>
        )
      })}
    </Card>
  )
}
